import type {
	API_CreateCustomerPlanRequest,
	API_CreateCustomerPlanResponse,
	API_ListOrganizationCustomerPlansResponse,
	API_UpdateCustomerPlanRequest,
	API_UpdateCustomerPlanResponse,
	CustomerPlan,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchOrganizationCustomerPlans = (
	token: string,
	organizationId: string,
): Promise<API_ListOrganizationCustomerPlansResponse> =>
	api
		.get(`/organizations/${organizationId}/customer-plans`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchCustomerPlanById = (
	token: string,
	organizationId: string,
	customerPlanId: string,
): Promise<CustomerPlan> =>
	api
		.get(`/organizations/${organizationId}/customer-plans/${customerPlanId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const createCustomerPlan = (
	token: string,
	organizationId: string,
	data: API_CreateCustomerPlanRequest,
): Promise<API_CreateCustomerPlanResponse> =>
	api
		.post(`/organizations/${organizationId}/customer-plans`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateCustomerPlan = (
	token: string,
	organizationId: string,
	customerPlanId: string,
	data: API_UpdateCustomerPlanRequest,
): Promise<API_UpdateCustomerPlanResponse> =>
	api
		.put(
			`/organizations/${organizationId}/customer-plans/${customerPlanId}`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const deleteCustomerPlan = (
	token: string,
	organizationId: string,
	customerPlanId: string,
): Promise<void> =>
	api
		.delete(
			`/organizations/${organizationId}/customer-plans/${customerPlanId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then(() => undefined);

// Query options
export const fetchOrganizationCustomerPlansQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organization_customer_plans", organizationId, userId],
		queryFn: () => fetchOrganizationCustomerPlans(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchCustomerPlanByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerPlanId: string,
) =>
	queryOptions({
		queryKey: ["customer_plan", organizationId, customerPlanId, userId],
		queryFn: () => fetchCustomerPlanById(token, organizationId, customerPlanId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateCustomerPlan = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateCustomerPlanRequest;
		}) => createCustomerPlan(token, organizationId, data),
		toastMessages: {
			loading: t("common.creating"),
			success: t("common.createSuccess"),
			error: t("common.createError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization customer plans
			queryClient.invalidateQueries({
				queryKey: ["organization_customer_plans", variables.organizationId],
			});
		},
	});
};

export const useUpdateCustomerPlan = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerPlanId,
			data,
		}: {
			token: string;
			organizationId: string;
			customerPlanId: string;
			data: API_UpdateCustomerPlanRequest;
		}) => updateCustomerPlan(token, organizationId, customerPlanId, data),
		toastMessages: {
			loading: t("common.updating"),
			success: t("common.updateSuccess"),
			error: t("common.updateError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization customer plans
			queryClient.invalidateQueries({
				queryKey: ["organization_customer_plans", variables.organizationId],
			});

			// Invalidate specific customer plan
			queryClient.invalidateQueries({
				queryKey: [
					"customer_plan",
					variables.organizationId,
					variables.customerPlanId,
				],
			});
		},
	});
};

export const useDeleteCustomerPlan = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerPlanId,
		}: {
			token: string;
			organizationId: string;
			customerPlanId: string;
		}) => deleteCustomerPlan(token, organizationId, customerPlanId),
		toastMessages: {
			loading: t("common.deleting"),
			success: t("common.deleteSuccess"),
			error: t("common.deleteError"),
		},
		showErrorToast: false, // We'll handle error toasts manually
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization customer plans
			queryClient.invalidateQueries({
				queryKey: ["organization_customer_plans", variables.organizationId],
			});

			// Remove specific customer plan from cache
			queryClient.removeQueries({
				queryKey: [
					"customer_plan",
					variables.organizationId,
					variables.customerPlanId,
				],
			});
		},
		onError: (error: AxiosError) => {
			// Handle specific error cases
			if (error.response?.status === 400) {
				// Extract the specific error message from the API response
				const errorData = error.response.data as { message?: string };
				const specificMessage = errorData?.message;

				if (specificMessage) {
					toast.error(specificMessage);
				} else {
					toast.error(t("common.deleteError"));
				}
			} else {
				// For all other errors, show the generic error message
				toast.error(t("common.deleteError"));
			}
		},
	});
};
